/**
 * @file   src\Pages\ManageAdminSubscription.tsx
 * @brief  Manage Admin Subscription page.
 * @date   JUL, 2024
 * @author ZCO Engineer
 * @copyright (c) 2024, ZCO
 */

import { useState, Container, Table, Badge, Button, Breadcrumb, useEffect, useOutletContext, useNavigate, Pagination, Modal, OverlayTrigger, Tooltip } from '../utils/thirdpartyComponents';
import "../styles/subscriptions.scss";
import EditIcon from '../assets/images/icons/editicon';
import DeleteIcon from '../assets/images/icons/deleteicon';
import CWSwitch from '../components/CWSwitch';
import CWInputGroup from '../components/CWInputGroup';
import AddIcon from '../assets/images/icons/addicon';
import { AppContextType } from '../interfaces/generalInterface';
import { INITIAL_FEE_MAX_LENGTH } from '../utils/constants';
import { pageURLs } from '../utils/pageUrls';
import axios from '../config/axiosConfig';
import { SubscriptionApi } from '../utils/apiUrls';
import { OwnerSubscriptionItem } from '../interfaces/ownerSubscriptionInterface';
import { MessageToaster } from '../utils/toastUtil';
import SmallLoader from '../components/SmallLoader';

const ManageAdminSubscription = () => {

    // Access the context
    const { data, updateData } = useOutletContext<AppContextType>();

    const [feeCharged, setFeeCharged] = useState<number>(0)

    const navigate = useNavigate()

    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const toaster = new MessageToaster();
    const [selectedSubscriptionId, setSelectedSubscriptionId] = useState<number | null>(null);

    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState<boolean>(false)
    const [showDisableFeeConfirmation, setShowDisableFeeConfirmation] = useState<boolean>(false)

    const [subscriptions, setSubscriptions] = useState<OwnerSubscriptionItem[]>([])

    // State to track switch status
    const [isInitialFee, setIsInitialFee] = useState(false);

    useEffect(() => {
        getOwnerSubscriptionList(currentPage)
        handleUpdate()
    }, [])

    // Update the data
    const handleUpdate = async () => {
        await updateData({ heading: "Manage Subscription", url: ``, showBackButton: false, showMainMenu: true, activeLink: "manage-admin-subscriptions" });
    };

    // To get subscription list
    const getOwnerSubscriptionList = async (pageNo = 1) => {
        try {
            setLoading(true)
            const { data } = await axios.get(SubscriptionApi.GET_OWNER_SUBSCRIPTION_LIST + `?pageNo=${pageNo}`);
            if (data?.error === 0 && Array.isArray(data?.data?.list)) {
                const owner_subscription_list: OwnerSubscriptionItem[] = data.data.list;
                const initialFeeStatus = data.data.initialFee.HasEnable === 1 ? true : false
                setIsInitialFee(initialFeeStatus)
                setFeeCharged(data.data.initialFee.InitialFee)
                if (owner_subscription_list.length > 0) {
                    setSubscriptions(owner_subscription_list)
                    setTotalPages(Math.ceil(data.data.totalCount / data.data.limit) || 1);
                } else {
                    console.log('Subscription list is empty');
                    setSubscriptions([]);  // Set to an empty array if subscription_list is empty
                }
            } else {
                console.log('Subscription list is not available or is not an array');
                setSubscriptions([]);  // Set to an empty array if data is not available
            }
            setLoading(false)
        } catch (error: any) {
            console.error('Error fetching subscription list:', error.message);
            setLoading(false)
            throw new Error(error.response?.data?.message || error.message);
        }
    }

    const handleSwitchChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        setIsInitialFee(e.target.checked);

        if (e.target.checked === false) {
            // Show confirmation popup when initial fee is disabled
            setShowDisableFeeConfirmation(true)
        }
    };

    const handlePageChange = (newPage: number) => {
        if (newPage > 0 && newPage <= totalPages) {
            setCurrentPage(newPage);
        }
    };

    const getVisiblePageNumbers = () => {
        const totalPageNumbersToShow = 5;
        let startPage = Math.max(1, currentPage - Math.floor(totalPageNumbersToShow / 2));
        let endPage = startPage + totalPageNumbersToShow - 1;

        if (endPage > totalPages) {
            endPage = totalPages;
            startPage = Math.max(1, endPage - totalPageNumbersToShow + 1);
        }

        const pageNumbers = [];
        for (let i = startPage; i <= endPage; i++) {
            pageNumbers.push(i);
        }

        return pageNumbers;
    };

    // Triggers whenever input is entered
    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        if (value.length <= INITIAL_FEE_MAX_LENGTH) {
            // Update the state only if the length is within the limit
            setFeeCharged(parseInt(value));
        }
    }

    // Navigate to add subscription page
    const addOwnerSubscription = () => {
        navigate(`/superadmin/${pageURLs.add_admin_subscription}`)
    }

    const confirmDelete = (id: number) => {
        setSelectedSubscriptionId(id)
        setShowDeleteConfirmation(true)
    }

    // Delete Subscription
    const deleteOwnerSubscription = async () => {
        try {
            setLoading(true)
            const { data } = await axios.post(SubscriptionApi.DELETE_OWNER_SUBSCRIPTION, { "subscriptionId": selectedSubscriptionId });
            setLoading(false)

            if (data?.error === 0) {
                navigate(`/superadmin/${pageURLs.manage_admin_subscription}`)
                setShowDeleteConfirmation(false)
                // Reload page
                getOwnerSubscriptionList(currentPage)
            }
            else {
                // show error message
                toaster.toastError(data?.message);
            }
        } catch (error: any) {
            throw new Error(error?.response?.data?.message || error?.message);
        }
    }

    //Hide ppopup
    const handleOnHide = () => {
        setShowDeleteConfirmation(false)
    }

    //Hide ppopup
    const handleOnInitialFeeHide = () => {
        setShowDisableFeeConfirmation(false)

        // Switch is enabled
        setIsInitialFee(true)
    }

    // If initial fee is disabled
    const disableInitialFee = async () => {
        setLoading(true)
        // Fee is passed as 0 
        await setFeeCharged(0)
        await updateInitialFee(false, 0)
        setShowDisableFeeConfirmation(false)

    }

    // To save updated initial fee
    const handleInitialFee = async (event: React.FormEvent<HTMLFormElement>, initialFeeEnabled: boolean) => {
        event.preventDefault()
        try {
            setLoading(true)
            updateInitialFee(initialFeeEnabled, feeCharged)
        } catch (error: any) {
            throw new Error(error?.response?.data?.message || error?.message);
        }
        finally {
            setLoading(false)
        }
    }

    const updateInitialFee = async (initialFeeEnabled: boolean, feeCharged: number) => {
        const { data } = await axios.post(SubscriptionApi.UPDATE_INITIAL_FEE, {
            "initialFeeEnabled": initialFeeEnabled,
            "initialFee": feeCharged
        });

        if (data?.error === 0) {
            setLoading(false)
            toaster.toastSuccess(data?.message)

        }
        else {
            // show error message
            toaster.toastError(data?.message);
            setLoading(false)
        }
    }

    // Truncate text function to limit the length of the description displayed
    const truncateText = (text: string, maxLength: number): string => {
        return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
    };
    // Render tooltip function for the full address
    const renderTooltip = (fullAddress: string) => (props: any) => (
        <Tooltip id="button-tooltip" {...props}>
            {fullAddress}
        </Tooltip>
    );
    return (

        <div className='manage-carwash-container manage-admin-subscription'>
            <Container>

                <div className="manage-header-block d-flex justify-content-end">
                    <Button
                        type="submit"
                        variant="primary"
                        className="primary-btn ms-auto d-flex align-items-center"
                        onClick={() => addOwnerSubscription()}
                        title='Add New Subscription'
                    >
                        Add New Subscription
                        <AddIcon />
                    </Button>
                </div>
                <div className="table-title-block">
                    <h5 className='f-bold'>Manage Subscription</h5>

                </div>
                <div className="cw-table-wrap">
                    <Table responsive className='m-0'>
                        <thead>
                            <tr>
                                <th>Subscription Name</th>
                                <th>Description</th>
                                <th>Commission Slabs</th>
                                <th>Charge</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {subscriptions && subscriptions.length > 0 ? (
                                subscriptions.map((item, index) => (
                                    <tr>
                                        <td>{item.SubscriptionName}</td>
                                        <td className='description' width="26%">{item.Description ? <>
                                                    <OverlayTrigger
                                                    placement="top"
                                                    overlay={renderTooltip(item.Description)} // Pass the full Description to the tooltip
                                                    >
                                                        <span>{truncateText(item.Description, 20)}</span>
                                                    </OverlayTrigger></> : ''}</td>
                                        <td>${item.SlabFrom} - ${item.SlabTo}</td>
                                        <td> {item.ChargeType === 1 ? `${item.Charge}%` : `$${item.Charge}`}</td>
                                        <td className='nowrap' width="10%">
                                            <div className="active-group d-flex align-items-center">
                                                <Button variant="light" className="light-btn action-btn" title='Edit' onClick={() => navigate(`/superadmin/${pageURLs.edit_admin_subscription}/${item.CarWashOwnerSubscriptionID}`)}><EditIcon /></Button>
                                                <Button variant="light" className="light-btn action-btn" title='Delete' onClick={() => confirmDelete(item.CarWashOwnerSubscriptionID)}><DeleteIcon /></Button>
                                            </div>
                                        </td>
                                    </tr>
                                ))) : (
                                <tr>
                                    <td style={{ textAlign: 'center' }} colSpan={7} >No data found.</td>
                                </tr>
                            )}
                        </tbody>
                    </Table>

                    {subscriptions.length ? <div className="mt-3 d-flex justify-content-end">
                        <Pagination>
                            <Pagination.First
                                disabled={currentPage === 1}
                                onClick={() => handlePageChange(1)}
                            />
                            <Pagination.Prev
                                disabled={currentPage === 1}
                                onClick={() => handlePageChange(currentPage - 1)}
                            />
                            {getVisiblePageNumbers().map((pageNumber) => (
                                <Pagination.Item
                                    key={pageNumber}
                                    active={pageNumber === currentPage}
                                    onClick={() => handlePageChange(pageNumber)}
                                >
                                    {pageNumber}
                                </Pagination.Item>
                            ))}
                            <Pagination.Next
                                disabled={currentPage === totalPages}
                                onClick={() => handlePageChange(currentPage + 1)}
                            />
                            <Pagination.Last
                                disabled={currentPage === totalPages}
                                onClick={() => handlePageChange(totalPages)}
                            />
                        </Pagination>
                    </div> : null}
                    <div className="fee-switch d-flex align-items-center">
                        <h5 className='f-bold'>Initial Fee Management</h5>
                        <CWSwitch
                            id="statusswitch"
                            name="statusswitch"
                            checked={isInitialFee}
                            onChange={handleSwitchChange}
                        />
                    </div>
                    {isInitialFee &&
                        <form onSubmit={(e) => handleInitialFee(e, true)} >
                            <>
                                <div className="fee-charge">
                                    <CWInputGroup
                                        id="feecharged"
                                        name="feecharged"
                                        type="number"
                                        label="Initial Fee Charged"
                                        labelPrefix="$"
                                        maxLength={INITIAL_FEE_MAX_LENGTH}
                                        value={feeCharged}
                                        onChange={handleInputChange}

                                    />
                                </div>
                                <div className="btn-sec d-flex justify-content-end">
                                    {/* <Button title="Cancel" variant="primary" className="secondary-btn" onClick={() => alert("Not Implemented.")}>Cancel</Button> */}
                                    <Button title="Save" variant="primary" className="primary-btn" type="submit" > Save</Button>
                                </div>
                            </>
                        </form>
                    }
                </div>

                <Modal
                    show={showDeleteConfirmation}
                    onHide={handleOnHide}
                    centered
                    className='add-modal'
                >
                    <Modal.Body className='text-center'>
                        <h5 className='f-bold text-center'>Are you sure you want to delete Subscription?</h5>
                        <div className="btn-sec d-grid">
                            <Button title='Cancel' variant="light" className="secondary-btn" onClick={handleOnHide}>Cancel</Button>
                            <Button title='Yes' variant="primary" className='primary-btn btn-main m-0' onClick={deleteOwnerSubscription}>Yes</Button>
                        </div>
                    </Modal.Body>
                    {loading && <SmallLoader />}
                </Modal>

                <Modal
                    show={showDisableFeeConfirmation}
                    onHide={handleOnInitialFeeHide}
                    centered
                    className='add-modal'
                >
                    <Modal.Body className='text-center'>
                        <h5 className='f-bold text-center'>Are you sure you want to disable Initial Fee?</h5>
                        <div className="btn-sec d-grid">
                            <Button title='Cancel' variant="light" className="secondary-btn" onClick={handleOnInitialFeeHide}>Cancel</Button>
                            <Button title='Yes' variant="primary" className='primary-btn btn-main m-0' onClick={disableInitialFee}>Yes</Button>
                        </div>
                    </Modal.Body>
                    {loading && <SmallLoader />}
                </Modal>

            </Container >
        </div >
    )
}

export default ManageAdminSubscription;
